/**
 * AiTasksController
 *
 * Controller for monitoring AI video generation tasks by polling their status
 * and notifying users when complete.
 *
 * Usage:
 *   app.aiTasksController.addTask(threadId);
 */
import app from 'app';
import Marionette from 'backbone.marionette';

const POLL_INTERVAL = 5; // seconds
const TASK_TIMEOUT_MINUTES = 10; // minutes
const STORAGE_KEY = 'aiTasksQueue';

app.namespace.Controller.AiTasksController = Marionette.Object.extend({
  initialize() {
    this.tasks = new Map();
    this._rehydrateTasksFromStorage();
  },

  /**
   * Log message in non-production environments
   * @private
   */
  _log(...args) {
    if (serverProfile.environment !== 'prod') {
      console.log('AiTasksController:', ...args);
    }
  },

  /**
   * Centralized storage mutation - always fetches fresh data before mutating
   * @private
   * @param {Function} mutatorFn - Function that receives threadIds array and returns modified array
   */
  _mutateStorage(mutatorFn) {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      const threadIds = data ? JSON.parse(data) : [];
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(mutatorFn(Array.isArray(threadIds) ? threadIds : []))
      );
    } catch (error) {
      console.error('AiTasksController: Storage error', error);
    }
  },

  /**
   * Get all threadIds from storage
   * @private
   * @returns {Array<string>} Array of threadIds
   */
  _getStoredTasks() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      const threadIds = data ? JSON.parse(data) : [];
      return Array.isArray(threadIds) ? threadIds : [];
    } catch (error) {
      console.error('AiTasksController: Error loading from storage', error);
      return [];
    }
  },

  /**
   * Save task to storage
   * @private
   * @param {string} threadId - The thread ID to save
   */
  _saveTaskToStorage(threadId) {
    this._mutateStorage((threadIds) =>
      threadIds.includes(threadId) ? threadIds : [...threadIds, threadId]
    );
  },

  /**
   * Remove task from storage
   * @private
   * @param {string} threadId - The thread ID to remove
   */
  _removeTaskFromStorage(threadId) {
    this._mutateStorage((threadIds) =>
      threadIds.filter((id) => id !== threadId)
    );
  },

  /**
   * Rehydrate tasks from storage on initialization
   * @private
   */
  _rehydrateTasksFromStorage() {
    const threadIds = this._getStoredTasks();
    if (!threadIds.length) return;

    this._log(`Rehydrating ${threadIds.length} task(s) from storage`);

    threadIds.forEach((threadId) => {
      if (threadId && typeof threadId === 'string') {
        this.addTask(threadId);
      }
    });
  },

  /**
   * Start polling for a new AI task
   * @param {string} threadId - The thread ID to monitor
   * @returns {boolean} - True if task was added, false if invalid or already exists
   */
  addTask(threadId) {
    // Validate input
    if (!threadId || typeof threadId !== 'string') {
      console.error('AiTasksController: Invalid threadId provided', threadId);
      return false;
    }

    if (this.tasks.has(threadId)) {
      console.warn(
        `AiTasksController: Task ${threadId} is already being monitored`
      );
      return false;
    }

    this._log(`Starting to monitor task ${threadId}`);

    // Create polling executor
    const executor = new app.namespace.Model.TimedExecutor({
      timeout: POLL_INTERVAL,
      run: this._pollTask.bind(this, threadId),
    });

    // Create timeout timer
    const timeout = new app.namespace.Util.Timer(() => {
      this._onTaskTimeout(threadId);
    }, app.TIME.minute(TASK_TIMEOUT_MINUTES));
    timeout.start();

    // Store task state
    this.tasks.set(threadId, {
      threadId,
      executor,
      timeout,
      startTime: Date.now(),
      currentXhr: null,
    });

    // Start polling
    executor.start();

    // Save to storage
    this._saveTaskToStorage(threadId);

    return true;
  },

  /**
   * Poll function called by TimedExecutor
   * @private
   * @param {string} threadId - The thread ID to check
   * @param {jQuery.Deferred} loop - Promise to resolve/reject for continuing polling
   */
  _pollTask(threadId, loop) {
    const task = this.tasks.get(threadId);
    if (!task) {
      loop.reject();
      return;
    }

    // Store the XHR so it can be aborted if needed
    const xhr = app.api.draftStories.checkAiTaskStatus(threadId);
    task.currentXhr = xhr;

    xhr
      .done((response) => {
        task.currentXhr = null;

        // Reset error counter on successful response
        this._log(`Task ${threadId} status: ${response.status}`);

        switch (response.status) {
          case 'completed':
            this._onTaskComplete(threadId, response);
            loop.reject(); // Stop polling
            break;

          case 'failed':
            this._onTaskFailed(threadId, response);
            loop.reject(); // Stop polling
            break;

          case 'processing':
            loop.resolve(); // Continue polling
            break;

          default:
            console.warn(
              `AiTasksController: Unknown status ${response.status} for task ${threadId}`
            );
            loop.resolve(); // Continue polling by default
        }
      })
      .fail((error) => {
        task.currentXhr = null;
        // All HTTP errors that don't have JSON responses should stop polling
        // (JSON error responses are handled in .done() with retry logic)
        console.error(
          `AiTasksController: Fatal error (${
            error.status || 'network'
          }) polling task ${threadId}`,
          error
        );
        loop.resolve(); // Continue polling
      });
  },

  /**
   * Clean up and remove a task
   * @param {string} threadId - The thread ID to remove
   */
  removeTask(threadId) {
    const task = this.tasks.get(threadId);
    if (!task) {
      return;
    }

    this._log(`Removing task ${threadId}`);

    // Abort any in-flight request
    if (task.currentXhr) {
      task.currentXhr.abort();
      task.currentXhr = null;
    }

    // Stop executor
    if (task.executor && task.executor.isRunning()) {
      task.executor.stop();
    }

    // Stop timeout
    if (task.timeout) {
      task.timeout.stop();
    }

    // Remove from map
    this.tasks.delete(threadId);

    // Remove from storage
    this._removeTaskFromStorage(threadId);
  },

  /**
   * Handle successful task completion
   * @private
   * @param {string} threadId - The thread ID
   * @param {Object} response - The API response
   */
  async _onTaskComplete(threadId, response) {
    const task = this.tasks.get(threadId);
    if (!task) {
      console.warn(
        `AiTasksController: Task ${threadId} already removed (race condition)`
      );
      return;
    }

    // await new Promise((resolve) => setTimeout(resolve, 10000));

    const duration = ((Date.now() - task.startTime) / 1000).toFixed(1);
    const storyId =
      response?.metadata?.duplicatedStoryId || response?.metadata?.storyId;
    this._log(
      `Task ${threadId} completed successfully in ${duration}s for story ${storyId}`
    );

    let storyPreview = null;

    // Fetch updated story preview to get latest thumbnail
    if (storyId) {
      try {
        storyPreview = await app.api.draftStoryPreviews.get(storyId);
        this._log(
          `Fetched updated story preview for ${storyId}, thumbnail: ${storyPreview.thumbnailPath}`
        );
      } catch (error) {
        console.error(
          `AiTasksController: Failed to fetch story preview for ${storyId}`,
          error
        );
      }
    }

    // debugger;
    const storyTitle =
      response.result?.title || storyPreview?.title || 'AI Generated Story';

    // Dispatch DRAFT_READY event to show rich notification with thumbnail
    app.notificationController.dispatchEvent({
      message: 'DONE',
      data: {
        event: 'DRAFT_READY',
        storyId: storyId || null,
        storyTitle,
        storyThumbnailUrl:
          storyPreview?.thumbnailPath ||
          response.metadata?.storyThumbnailUrl ||
          null,
      },
    });

    // Update dashboard store with new thumbnail. `storyId` is the resulting
    // draft (a NEW version when Edit-with-AI produced a duplicate);
    // `metadata.storyId` is always the source the request was launched from.
    if (storyId) {
      app.rootStore.dispatch('AI_DRAFT_READY', {
        storyId: storyId,
        sourceStoryId: response?.metadata?.storyId || null,
        preview: storyPreview || null,
        thumbnailPath: storyPreview?.thumbnailPath || null,
        storyTitle,
      });
    }

    // Clean up
    this.removeTask(threadId);
  },

  /**
   * Handle task failure
   * @private
   * @param {string} threadId - The thread ID
   * @param {Object} response - The API response
   */
  _onTaskFailed(threadId, response) {
    const task = this.tasks.get(threadId);
    const errorMessage = response.error || 'Unknown error';
    if (!task) {
      console.warn(
        `AiTasksController: Task ${threadId} already removed (race condition)`
      );
      return;
    }

    this._log(`Task ${threadId} fail with error: ${errorMessage}`);

    const storyId =
      response?.metadata?.duplicatedStoryId || response?.metadata?.storyId;

    // If we have story info, show rich notification with error state
    if (storyId) {
      app.notificationController.dispatchEvent({
        message: 'FAILED',
        data: {
          event: 'DRAFT_READY',
          storyId: storyId,
          storyTitle: response.metadata.storyTitle || 'AI Generated Story',
          storyThumbnailUrl: response.metadata.storyThumbnailUrl || null,
        },
      });
      // Notify user with specific error message
      app.notificationController.push({
        text: `AI draft generation error: ${errorMessage}`,
        type: 'ERROR',
        timeout: app.TIME.minute(1),
      });

      // Update dashboard store. `storyId` may be the (never-inserted)
      // duplicate on an Edit-with-AI job; `metadata.storyId` is always the
      // SOURCE row that was locked for the job and must be re-unlocked.
      if (storyId) {
        app.rootStore.dispatch('AI_DRAFT_FINISHED', {
          storyId: storyId,
          sourceStoryId: response?.metadata?.storyId || null,
          success: false,
        });
      }
    } else {
      // Fallback to simple notification
      app.notificationController.push({
        text: 'AI draft generation failed. Please try again.',
        type: 'ERROR',
        timeout: app.TIME.minute(1),
      });
    }

    // Clean up
    this.removeTask(threadId);
  },

  /**
   * Handle task timeout
   * @private
   * @param {string} threadId - The thread ID
   */
  _onTaskTimeout(threadId) {
    const task = this.tasks.get(threadId);
    if (!task) {
      console.warn(
        `AiTasksController: Task ${threadId} already removed (race condition)`
      );
      return;
    }

    this._log(
      `Task ${threadId} timed out after ${TASK_TIMEOUT_MINUTES} minutes`
    );

    // Notify user
    app.notificationController.push({
      text: 'AI draft generation timed out. Please try again.',
      type: 'ERROR',
      timeout: app.TIME.minute(1),
    });

    // Clean up
    this.removeTask(threadId);
  },

  /**
   * Get all active task IDs
   * @returns {Array<string>}
   */
  getActiveTasks() {
    return Array.from(this.tasks.keys());
  },

  /**
   * Check if a task is being monitored
   * @param {string} threadId - The thread ID to check
   * @returns {boolean}
   */
  hasTask(threadId) {
    return this.tasks.has(threadId);
  },

  /**
   * Cleanup when controller is destroyed
   * Stop all active tasks and their timers
   */
  onBeforeDestroy() {
    this._log('Cleaning up all tasks');
    const taskIds = Array.from(this.tasks.keys());
    taskIds.forEach((threadId) => {
      this.removeTask(threadId);
    });
  },
});
